import React, { useState, useEffect } from 'react';
import { Button } from '../../Components/ui/button';
import { Input } from '../../Components/ui/input';
import { CaretSortIcon, FileIcon } from '@radix-ui/react-icons';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import { StopwatchIcon, CheckIcon } from '@radix-ui/react-icons';
import DropdownMenuDemo from '../../Components/Filter/AdminAudienceDropdown';
import * as XLSX from 'xlsx';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { RootState } from '@/src/State/store';
import { Toaster } from '../../Components/ui/toaster';
import { convertUTCToLocal } from '../../../src/utils/convertUTCToLocal';

interface Audience {
  partnerId: number;
  partnerName: string;
  partnerStatus: string;
  lastUpdatedTime: string;
  contactCount: number;
}

type SortField = keyof Pick<
  Audience,
  'partnerName' | 'partnerStatus' | 'lastUpdatedTime' | 'contactCount'
>;

type SortOrder = 'asc' | 'desc';

interface SortConfig {
  field: SortField | null;
  order: SortOrder;
}

const Audiences: React.FC = () => {
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [audienceList, setAudienceList] = useState<Audience[]>([]);
  const [rawAudienceList, setRawAudienceList] = useState<Audience[]>([]);
  const [checkboxSelectedRows, setCheckboxSelectedRows] = useState<number[]>(
    [],
  );
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [contactCountLoading, setContactCountLoading] = useState<{
    [key: number]: boolean;
  }>({});

  const workspaceTimezone = useSelector(
    (state: RootState) => state.authentication.workspaceTimezone,
  );

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: null,
    order: 'asc',
  });

  const [filterData, setFilterData] = useState({
    filter: 'None',
    value: '',
  });
  const [dateList, setDateList] = useState<string[]>([]);

  const apiUrlAdminAcc = useSelector(
    (state: RootState) => state.authentication.adminUrl,
  );

  const handleCheckboxRowSelect = (id: number) => {
    setCheckboxSelectedRows((prev) => {
      const newSelectedRows = prev.includes(id)
        ? prev.filter((rowId) => rowId !== id)
        : [...prev, id];
      setIsAllSelected(newSelectedRows.length === audienceList.length);
      return newSelectedRows;
    });
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setCheckboxSelectedRows([]);
    } else {
      const allIds = audienceList.map((audience) => audience.partnerId);
      setCheckboxSelectedRows(allIds);
    }
    setIsAllSelected(!isAllSelected);
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when search or filter changes (not sort)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterData]);

  // Fetch data when dependencies change (excluding sortConfig)
  useEffect(() => {
    if (apiUrlAdminAcc) {
      getAudienceList();
    }
  }, [
    apiUrlAdminAcc,
    currentPage,
    rowsPerPage,
    debouncedSearchTerm,
    filterData,
  ]);

  const getAudienceList = async () => {
    if (!hasLoadedOnce) {
      setIsInitialLoading(true);
    } else {
      setIsTableLoading(true);
    }

    // Set loading state for all partnerIds
    const loadingStates = audienceList.reduce(
      (acc, audience) => {
        acc[audience.partnerId] = true;
        return acc;
      },
      {} as { [key: number]: boolean },
    );
    setContactCountLoading(loadingStates);
    debugger;
    try {
      const response = await axios.get(
        `${apiUrlAdminAcc}/GetContactCountsOperatorForAdmin`,
        {
          params: {
            pageNumber: currentPage,
            pageSize: rowsPerPage,
            searchTerm: debouncedSearchTerm,
            criteria: filterData.filter !== 'None' ? filterData.filter : null,
            subFilter: filterData.filter !== 'None' ? filterData.value : null,
          },
        },
      );

      const result = response.data;
      const rawData = (result.summaryDetails || []).map((item: any) => ({
        partnerId: item.partnerId,
        partnerName: item.partnerName,
        partnerStatus: item.partnerStatus,
        lastUpdatedTime: item.lastUpdatedTime,
        contactCount: parseInt(item.contactCount, 10) || 0,
      }));

      const filteredData = debouncedSearchTerm
        ? rawData.filter((audience: Audience) =>
            audience.partnerName
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase()),
          )
        : rawData;

      // Clear loading state after data is received
      const newloadingStates = audienceList.reduce(
        (acc: { [key: number]: boolean }, audience) => {
          acc[audience.partnerId] = false;
          return acc;
        },
        {} as { [key: number]: boolean },
      );

      setContactCountLoading(newloadingStates);

      setAudienceList(filteredData);
      setRawAudienceList(rawData);
      setTotalCount(result.totalCount || 0);
      setDateList(result.availableDates || []);
    } catch (error) {
      console.error('Error fetching audience list:', error);
      setAudienceList([]);
      setRawAudienceList([]);
      setTotalCount(0);
    } finally {
      setIsInitialLoading(false);
      setIsTableLoading(false);
      setHasLoadedOnce(true);
    }
  };

  // Apply frontend sorting when sortConfig or rawAudienceList changes
  useEffect(() => {
    const sortedData = getSortedData(rawAudienceList);
    setAudienceList(sortedData);
  }, [sortConfig, rawAudienceList]);

  // Sorting function - now only updates sort config, no API call
  const handleSort = (field: SortField) => {
    setSortConfig((prevConfig) => ({
      field,
      order:
        prevConfig.field === field && prevConfig.order === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  // Frontend sorting function
  const getSortedData = (data: Audience[]) => {
    if (!data || !sortConfig.field) return data || [];

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.field!];
      const bValue = b[sortConfig.field!];

      if (sortConfig.field === 'lastUpdatedTime') {
        const dateA = new Date(aValue as string).getTime();
        const dateB = new Date(bValue as string).getTime();
        return sortConfig.order === 'asc' ? dateA - dateB : dateB - dateA;
      }

      if (sortConfig.field === 'contactCount') {
        const numA = Number(aValue);
        const numB = Number(bValue);
        return sortConfig.order === 'asc' ? numA - numB : numB - numA;
      }

      if (sortConfig.field === 'partnerStatus') {
        // Custom status sorting order: "Syncing..." comes first, then "Updated"
        const statusOrder = { 'Syncing...': 0, active: 1 };
        const orderA = statusOrder[aValue as keyof typeof statusOrder] ?? 2;
        const orderB = statusOrder[bValue as keyof typeof statusOrder] ?? 2;

        if (orderA !== orderB) {
          return sortConfig.order === 'asc' ? orderA - orderB : orderB - orderA;
        }

        // If same status priority, sort alphabetically
        const stringA = String(aValue).toLowerCase();
        const stringB = String(bValue).toLowerCase();
        if (stringA < stringB) return sortConfig.order === 'asc' ? -1 : 1;
        if (stringA > stringB) return sortConfig.order === 'asc' ? 1 : -1;
        return 0;
      }

      // String comparison for workspaceName and other string fields
      const stringA = String(aValue).toLowerCase();
      const stringB = String(bValue).toLowerCase();

      if (stringA < stringB) return sortConfig.order === 'asc' ? -1 : 1;
      if (stringA > stringB) return sortConfig.order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (field: SortField) => {
    return (
      <CaretSortIcon
        className={`w-4 h-4 cursor-pointer ${
          sortConfig.field === field
            ? sortConfig.order === 'asc'
              ? 'text-gray-700 rotate-180'
              : 'text-gray-700'
            : 'text-gray-400'
        } transition-all duration-200`}
      />
    );
  };

  const renderStatusIcon = (status: string) => {
    switch (status) {
      case 'Syncing...':
        return <StopwatchIcon className='text-gray-500' />;
      case 'active':
        return (
          <CheckIcon className='text-gray-500 rounded-full border border-gray-500' />
        );
      default:
        return null;
    }
  };

  // const handleExportButtonClick = () => {
  //   const filteredData = audienceList.map(
  //     ({
  //       partnerId,
  //       partnerName,
  //       partnerStatus,
  //       contactCount,
  //       lastUpdatedTime,
  //     }) => ({
  //       partnerId,
  //       partnerName,
  //       partnerStatus,
  //       recipients: contactCount,
  //       lastUpdatedTime,
  //     })
  //   );

  //   const worksheet = XLSX.utils.json_to_sheet(filteredData);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Admin_Audience_List");
  //   XLSX.writeFile(workbook, "AdminAudienceList.xlsx");
  // };

  const handleExportButtonClick = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(
        `${apiUrlAdminAcc}/GetContactCountsOperatorForAdminWithOutPagination`,
        {
          params: {
            searchTerm: debouncedSearchTerm,
            criteria: filterData.filter !== 'None' ? filterData.filter : null,
            subFilter: filterData.filter !== 'None' ? filterData.value : null,
          },
        },
      );

      const result = response.data;
      const rawData: Audience[] = (result.summaryDetails || []).map(
        (item: any) => ({
          partnerId: item.partnerId,
          partnerName: item.partnerName,
          partnerStatus: item.partnerStatus,
          lastUpdatedTime: item.lastUpdatedTime,
          contactCount: parseInt(item.contactCount, 10) || 0,
        }),
      );

      const fullAudienceList = debouncedSearchTerm
        ? rawData.filter((audience) =>
            audience.partnerName
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase()),
          )
        : rawData;

      // If any checkboxes are selected, export only those rows; otherwise export all
      const dataToExport =
        checkboxSelectedRows.length > 0
          ? fullAudienceList.filter((audience) =>
              checkboxSelectedRows.includes(audience.partnerId),
            )
          : fullAudienceList;

      const filteredData = dataToExport.map(
        ({
          partnerId,
          partnerName,
          partnerStatus,
          contactCount,
          lastUpdatedTime,
        }) => ({
          partnerId,
          partnerName,
          partnerStatus,
          recipients: contactCount,
          lastUpdatedTime,
        }),
      );

      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Admin_Audience_List');
      XLSX.writeFile(workbook, 'AdminAudienceList.xlsx');
    } catch (error) {
      console.error('Error exporting audience list:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Show initial loading spinner
  if (isInitialLoading) {
    return (
      <div>
        <ToastContainer />
        <div className='flex flex-col items-center justify-center h-[500px]'>
          <CircularProgress color='primary' />
        </div>
      </div>
    );
  }

  if (
    !isInitialLoading &&
    !hasLoadedOnce &&
    totalCount === 0 &&
    !debouncedSearchTerm &&
    filterData.filter === 'None'
  ) {
    return (
      <div>
        <Toaster />
        <div className='flex flex-col items-center justify-center h-[500px]'>
          <h2 className='text-[24px] font-bold mb-1 text-[#000000]'>
            Here you will see all your audience lists
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />

      {/* Search and Filter Controls */}
      <div className='flex mt-2'>
        <div>
          <Input
            placeholder='Search audience by name...'
            className='w-[350px] text-[14px] font-normal text-[#64748B]'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className='flex items-end ml-auto'>
          <DropdownMenuDemo
            setFilterData={setFilterData}
            filterData={filterData}
            dateList={dateList}
          />
          <Button
            variant='outline'
            className='w-full mb-6 ml-4 mt-[-6] text-[#020617] font-medium text-[14px] py-2 px-3'
            onClick={handleExportButtonClick}
            disabled={audienceList.length === 0 || isExporting}
          >
            {isExporting ? (
              'Exporting...'
            ) : (
              <>
                <FileIcon
                  style={{ width: '14px', height: '14px' }}
                  className='mr-1 text-[#020617]'
                />
                Export
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className='rounded-md border overflow-hidden'>
        <div className='max-h-[60vh] overflow-y-auto'>
          <Table
            className='rounded-xl whitespace-nowrap border-gray-200'
            style={{ color: '#020202', fontSize: '15px' }}
          >
            <TableHeader className='text-center text-[14px] font-medium'>
              <TableRow className='sticky top-0 bg-white z-10 hover:bg-white'>
                <TableHead>
                  <div className='flex items-center gap-6 justify-start cursor-pointer'>
                    <input
                      type='checkbox'
                      className={`text-muted-foreground ${
                        isAllSelected
                          ? 'accent-gray-700 bg-grey-700 text-red-500'
                          : ''
                      }`}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      disabled={audienceList.length === 0}
                    />
                    <div
                      className='flex items-center gap-2 cursor-pointer hover:text-gray-700'
                      onClick={() => handleSort('partnerName')}
                    >
                      <span className='font-medium text-[14px] text-[#64748B]'>
                        Telco
                      </span>
                      {renderSortIcon('partnerName')}
                    </div>
                  </div>
                </TableHead>
                <TableHead style={{ width: '15%' }} className='text-left'>
                  <div
                    className='flex items-center gap-2 cursor-pointer hover:text-gray-700'
                    onClick={() => handleSort('partnerStatus')}
                  >
                    <span className='font-medium text-[14px] text-[#64748B]'>
                      Status
                    </span>
                    {renderSortIcon('partnerStatus')}
                  </div>
                </TableHead>
                <TableHead style={{ width: '20%' }}>
                  <div
                    className='flex items-center gap-2 justify-start cursor-pointer hover:text-gray-700'
                    onClick={() => handleSort('lastUpdatedTime')}
                  >
                    <span className='font-medium text-[14px] text-[#64748B]'>
                      Updated at
                    </span>
                    {renderSortIcon('lastUpdatedTime')}
                  </div>
                </TableHead>
                <TableHead style={{ width: '10%' }}>
                  <div
                    className='flex items-center gap-2 justify-center mr-2 cursor-pointer hover:text-gray-700'
                    onClick={() => handleSort('contactCount')}
                  >
                    <span className='font-medium text-[14px] text-[#64748B]'>
                      Active Roamers
                    </span>
                    {renderSortIcon('contactCount')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody className='text-left text-[14px] font-normal text-[#020617]'>
              {isTableLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className='text-center py-4'>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : audienceList.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className='text-center py-4 text-[#64748B]'
                  >
                    {debouncedSearchTerm || filterData.filter !== 'None'
                      ? 'No audience list found for the search term'
                      : 'No audience lists available'}
                  </TableCell>
                </TableRow>
              ) : (
                audienceList.map((audience) => {
                  const isSelected = checkboxSelectedRows.includes(
                    audience.partnerId,
                  );

                  return (
                    <TableRow
                      key={audience.partnerId}
                      className={`${isSelected ? 'bg-gray-200' : ''}`}
                    >
                      <TableCell className='flex justify-start py-4 text-green-900'>
                        <div className='flex items-center gap-6'>
                          <input
                            type='checkbox'
                            className='accent-gray-700 bg-grey-700 text-red-500'
                            checked={isSelected}
                            onChange={() =>
                              handleCheckboxRowSelect(audience.partnerId)
                            }
                          />
                          <span
                            style={{
                              color: '#020617',
                              fontSize: '14px',
                              fontWeight: '400',
                            }}
                          >
                            {audience.partnerName}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell style={{ width: '15%' }} className='py-4'>
                        <div className='flex items-center gap-2'>
                          <span className='flex-shrink-0'>
                            {renderStatusIcon(audience.partnerStatus)}
                          </span>
                          <span
                            style={{
                              color: '#020617',
                              fontSize: '14px',
                              fontWeight: '400',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {audience.partnerStatus}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell style={{ width: '20%' }} className='py-4'>
                        <div className='flex items-center gap-2'>
                          <span
                            style={{
                              color: '#020617',
                              fontSize: '14px',
                              fontWeight: '400',
                            }}
                          >
                            {audience.lastUpdatedTime &&
                            audience.lastUpdatedTime !== '-'
                              ? audience.lastUpdatedTime
                              : '-'}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell
                        style={{ width: '10%' }}
                        className='py-4 text-center mr-2'
                      >
                        <span
                          style={{
                            color: '#020617',
                            fontSize: '14px',
                            fontWeight: '400',
                          }}
                        >
                          {contactCountLoading[audience.partnerId]
                            ? 'Loading...'
                            : audience.contactCount}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className='flex justify-between items-center mt-4'>
        <div className='flex items-center space-x-2 text-gray-500 text-sm'>
          <span>{`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
            currentPage * rowsPerPage,
            totalCount,
          )} of ${totalCount} row(s)`}</span>
        </div>

        <div className='flex items-center space-x-4 font-medium text-sm'>
          <span className='text-[#020617] font-medium text-[14px]'>
            Rows per page
          </span>

          <div className='relative inline-block ml-2'>
            <select
              className='cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-yellow-400'
              style={{
                width: '60px',
                height: '30px',
                textAlign: 'left',
                fontSize: '12px',
                fontWeight: '400',
                borderColor: '#E5E7EB',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
              }}
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[5, 10, 20].map((num) => (
                <option
                  className='text-[12px] font-normal'
                  key={num}
                  value={num}
                >
                  {num}
                </option>
              ))}
            </select>
            <CaretSortIcon className='absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4' />
          </div>

          <div className='ml-4 mr-4'>
            <span className='text-[#020617] text-[14px] font-medium'>{`Page ${currentPage} of ${totalPages}`}</span>
          </div>

          <div className='flex items-center gap-2'>
            <button
              disabled={currentPage === 1}
              className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                currentPage === 1
                  ? 'cursor-not-allowed bg-gray-100'
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => handlePageChange(1)}
            >
              «
            </button>
            <button
              disabled={currentPage === 1}
              className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                currentPage === 1
                  ? 'cursor-not-allowed bg-gray-100'
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
            <button
              disabled={currentPage === totalPages}
              className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                currentPage === totalPages
                  ? 'cursor-not-allowed bg-gray-100'
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
            <button
              disabled={currentPage === totalPages}
              className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                currentPage === totalPages
                  ? 'cursor-not-allowed bg-gray-100'
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Audiences;
